// src/Canvas.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import MainLayout from "../Layouts/MainLayout";
import { getCachedImage, storeCachedImage } from "../helpers/imageCache";

// (Optional) If you pulled these helpers into a separate utils file, import them.
// Otherwise, you can leave them directly in this file.
async function fetchAlchemyMetadata({ contractAddress, tokenIdHex }) {
	const backendBase = "https://qs10n5ia26.execute-api.us-east-2.amazonaws.com/prod";
	const endpoint = `${backendBase}/getNFTMetadata?contractAddress=${contractAddress}&tokenIdHex=${tokenIdHex}`;

	const res = await fetch(endpoint);
	if (!res.ok) {
		throw new Error("Failed to fetch metadata from your backend.");
	}

	const data = await res.json();
	const imageUrl =
		data?.image?.pngUrl || data?.image?.originalUrl || "/images/assets/placeholder.png";

	return imageUrl;
}

/**
 * Snap an angle if it's within a threshold of a multiple of `snapAngle`.
 * Otherwise return the original angle.
 *
 * e.g. snapAngle=45, threshold=5 => snap near 0°, 45°, 90°, etc.
 */
function maybeSnapAngle(rawAngle, snapAngle = 45, threshold = 5) {
	// Convert the rawAngle to its nearest multiple of `snapAngle`
	const nearestMultiple = Math.round(rawAngle / snapAngle) * snapAngle;

	if (Math.abs(rawAngle - nearestMultiple) <= threshold) {
		return nearestMultiple;
	} else {
		return rawAngle;
	}
}

// Helper to word-wrap text on spaces & handle "\n"
function wrapText(ctx, text, maxWidth) {
	const lines = [];

	const paragraphs = text.split("\n");
	paragraphs.forEach((paragraph) => {
		const words = paragraph.split(" ");
		let currentLine = words[0] || "";

		for (let i = 1; i < words.length; i++) {
			const testLine = currentLine + " " + words[i];
			const metrics = ctx.measureText(testLine.trim());
			if (metrics.width > maxWidth) {
				lines.push(currentLine);
				currentLine = words[i];
			} else {
				currentLine = testLine;
			}
		}
		lines.push(currentLine);
	});

	return lines.map((l) => l.trim());
}

// Helper to measure multiline text (with wrapping)
function getMultilineTextMetrics(item, ctx, bgRect) {
	const { text, fontSize, fontFamily, isBold, isItalic, isAllCaps } = item;

	// Build the font descriptor
	let fontDesc = "";
	if (isItalic) fontDesc += "italic ";
	if (isBold) fontDesc += "bold ";
	fontDesc += `${fontSize}px ${fontFamily || "Impact"}`;
	ctx.font = fontDesc;

	// Possibly uppercase
	const displayText = isAllCaps ? text.toUpperCase() : text;

	// The background’s width:
	let maxWidth = bgRect.width - 10;
	if (maxWidth < 0) maxWidth = 50; // safety

	let lines;
	if (item.recalculateWrap) {
		// Re-run your existing wrap logic
		lines = wrapText(ctx, displayText, maxWidth);

		// Store these lines in the layer, so we can preserve them
		item.storedLines = lines;

		// Now that we've recalculated, we can mark false
		item.recalculateWrap = false;
	} else {
		// Do NOT re-wrap: just use item.storedLines
		lines = item.storedLines || [displayText];
		// (fallback if storedLines is null for any reason)
	}

	// Next, measure those lines
	let boundingBoxW = 0;
	let boundingBoxH = 0;
	const lineGap = 7;
	const lineMetrics = [];

	for (const line of lines) {
		const measure = ctx.measureText(line);
		const ascent = measure.actualBoundingBoxAscent ?? fontSize * 0.8;
		const descent = measure.actualBoundingBoxDescent ?? fontSize * 0.2;
		const lineHeight = ascent + descent + lineGap;

		if (measure.width > boundingBoxW) {
			boundingBoxW = measure.width;
		}
		boundingBoxH += lineHeight;

		lineMetrics.push({
			text: line,
			width: measure.width,
			ascent,
			descent,
			lineHeight,
		});
	}

	return {
		lines,
		lineMetrics,
		boundingBoxW,
		boundingBoxH,
	};
}

// Clamp text position inside the canvas
function clampTextInsideBgRect(layer, ctx, bgRect) {
	// First, measure text bounding box:
	const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(layer, ctx, bgRect);

	const halfW = boundingBoxW / 2;
	const halfH = boundingBoxH / 2;

	// The background rectangle is:
	//   x: bgRect.x
	//   y: bgRect.y
	//   width: bgRect.width
	//   height: bgRect.height

	// So clamp the text’s center (layer.x, layer.y) so the box never leaves the BG:
	layer.x = Math.max(bgRect.x + halfW, Math.min(bgRect.x + bgRect.width - halfW, layer.x));
	layer.y = Math.max(bgRect.y + halfH, Math.min(bgRect.y + bgRect.height - halfH, layer.y));
}

function Canvas() {
	// ========== State for Tabs ==========
	const [activeTab, setActiveTab] = useState("background");
	const [assetsAreReady, setAssetsAreReady] = useState(false);
	const [loadingAssets, setLoadingAssets] = useState(false);
	// ========== Text Settings State ==========
	const [selectedTextIsWhite, setSelectedTextIsWhite] = useState(true);
	const [selectedFontFamily, setSelectedFontFamily] = useState("Impact");
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);
	const [isUnderlined, setIsUnderlined] = useState(false);
	const [isStrikethrough, setIsStrikethrough] = useState(false);
	const [isAllCaps, setIsAllCaps] = useState(true);
	const [textOpacity, setTextOpacity] = useState(1.0);

	// ========== Text Input Fields ==========
	const [topTextInput, setTopTextInput] = useState("");
	const [bottomTextInput, setBottomTextInput] = useState("");
	const [extraTextInputs, setExtraTextInputs] = useState([]); // multiple extra lines

	// ========== Background Image State ==========
	const [bgImage, setBgImage] = useState(null);
	const [uploadedImage, setUploadedImage] = useState(null);
	const uploadedImageObj = useRef(null);
	const originalBgDataRef = useRef(null);

	// We store a rectangle for the BG draw coords
	const bgRect = useRef({ x: 0, y: 0, width: 0, height: 0 });

	// ========== Canvas and Layers ==========
	const canvasRef = useRef(null);
	const layers = useRef([]); // All items (assets/text)
	const [layersVersion, setLayersVersion] = useState(0);
	const [anySelected, setAnySelected] = useState(false);

	// For deep-fry steps
	const preDeepFryCanvasRef = useRef(null);
	const preDeepFryLayersRef = useRef([]);
	const [hasDeepFried, setHasDeepFried] = useState(false);

	// ========== Misc State ==========
	const [canvasHeight, setCanvasHeight] = useState(0);
	const isMobile = window.innerWidth <= 768;

	// ========== Category for Assets ==========
	const [category, setCategory] = useState("Category");
	const [isAssetAdded, setIsAssetAdded] = useState(false);

	// ========== Deep Fry Sliders ==========
	const [satValue, setSatValue] = useState(2.0);
	const [conValue, setConValue] = useState(2.0);
	const [briValue, setBriValue] = useState(2.4);
	const [hueValue, setHueValue] = useState(-5);
	const [pixValue, setPixValue] = useState(0);
	const [noiseValue, setNoiseValue] = useState(40);
	const [satEnabled, setSatEnabled] = useState(true);
	const [conEnabled, setConEnabled] = useState(true);
	const [briEnabled, setBriEnabled] = useState(true);
	const [hueEnabled, setHueEnabled] = useState(true);
	const [pixEnabled, setPixEnabled] = useState(true);
	const [noiseEnabled, setNoiseEnabled] = useState(true);

	// ========== Eraser ==========
	const [isErasing, setIsErasing] = useState(false);
	const [eraserSize, setEraserSize] = useState(20);
	const eraserStrokesRef = useRef([]);
	const isMouseDownInEraseMode = useRef(false);

	// ========== Helpers ==========
	const forceUpdateLayers = useCallback(() => {
		setLayersVersion((v) => v + 1);
	}, []);
	const updateAnySelected = () => {
		const anyLayerSelected = layers.current.some((t) => t.isSelected);
		setAnySelected(anyLayerSelected);
	};
	const isAnyItemSelected = () => layers.current.some((layer) => layer.isSelected);

	// ========== Handlers for NFT, BG, Eraser, etc. ==========

	const handleRemoveBackground = useCallback(() => {
		if (uploadedImageObj.current) {
			uploadedImageObj.current = null;
			setUploadedImage(null);
			setBgImage(null);
			eraserStrokesRef.current = [];
			// Then re-draw
			drawCanvas();
		}
	}, []);

	const handleUndoLastErase = () => {
		if (eraserStrokesRef.current.length > 0) {
			eraserStrokesRef.current.pop();
			drawCanvas();
		}
	};

	const handleClearAllErases = () => {
		eraserStrokesRef.current = [];
		drawCanvas();
	};

	// ========== Upload / Draw Image ==========

	const handleImageUpload = useCallback((file) => {
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (evt) => {
			const img = new Image();
			img.onload = () => {
				setBgImage(img);
				setUploadedImage(img);
				uploadedImageObj.current = img;

				// Save raw BG:
				const tempCanvas = document.createElement("canvas");
				tempCanvas.width = img.width;
				tempCanvas.height = img.height;
				const tCtx = tempCanvas.getContext("2d");
				tCtx.drawImage(img, 0, 0);
				originalBgDataRef.current = tempCanvas.toDataURL("image/png");

				drawCanvas();
			};
			img.src = evt.target.result;
		};
		reader.readAsDataURL(file);
	}, []);

	// ========== Canvas Drawing ==========

	const drawCanvas = useCallback((hideSelection = false) => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";

		// BG image
		if (uploadedImageObj.current) {
			drawImageInCanvas(uploadedImageObj.current, canvas, ctx);
		}

		// Layers
		layers.current.forEach((layer) => {
			if (layer.type === "asset") {
				// draw asset
				ctx.save();
				const centerX = layer.x + layer.width / 2;
				const centerY = layer.y + layer.height / 2;
				ctx.translate(centerX, centerY);
				ctx.rotate((layer.rotation * Math.PI) / 180);

				if (layer.isFlipped) {
					ctx.scale(-1, 1);
				}

				ctx.drawImage(layer.image, -layer.width / 2, -layer.height / 2, layer.width, layer.height);
				ctx.restore();

				if (layer.isSelected && !hideSelection) {
					drawAssetSelectionBox(ctx, layer);
				}
			} else if (layer.type === "text") {
				drawTextItem(ctx, layer, hideSelection);
			}
		});

		// Eraser strokes
		if (eraserStrokesRef.current.length > 0) {
			ctx.save();
			ctx.globalCompositeOperation = "destination-out";
			eraserStrokesRef.current.forEach((strokeObj) => {
				ctx.lineCap = "round";
				ctx.lineJoin = "round";
				ctx.lineWidth = strokeObj.size;
				ctx.strokeStyle = "rgba(0,0,0,1.0)";
				const path = strokeObj.path;
				if (path.length > 1) {
					ctx.beginPath();
					ctx.moveTo(path[0].x, path[0].y);
					for (let i = 1; i < path.length; i++) {
						ctx.lineTo(path[i].x, path[i].y);
					}
					ctx.stroke();
				}
			});
			ctx.restore();
		}
	}, []);

	// A small function to draw the BG image inside the canvas (centered)
	// Canvas.js

	function drawImageInCanvas(img, canvas, ctx) {
		const canvasWidth = canvas.width;
		const canvasHeight = canvas.height;
		const imgWidth = img.width;
		const imgHeight = img.height;

		// Fit image in (canvasWidth x canvasHeight), preserving aspect ratio
		const aspectRatio = imgWidth / imgHeight;
		let drawWidth, drawHeight;

		if (aspectRatio > 1) {
			// If the image is "landscape"
			drawWidth = canvasWidth;
			drawHeight = canvasWidth / aspectRatio;
			if (drawHeight > canvasHeight) {
				// If that’s still too tall, adjust again
				drawHeight = canvasHeight;
				drawWidth = canvasHeight * aspectRatio;
			}
		} else {
			// "Portrait" or square
			drawHeight = canvasHeight;
			drawWidth = canvasHeight * aspectRatio;
			if (drawWidth > canvasWidth) {
				// If that’s still too wide, adjust again
				drawWidth = canvasWidth;
				drawHeight = canvasWidth / aspectRatio;
			}
		}

		// Center the image
		const offsetX = (canvasWidth - drawWidth) / 2;
		const offsetY = (canvasHeight - drawHeight) / 2;

		ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

		// Save these for clamping logic:
		bgRect.current = {
			x: offsetX,
			y: offsetY,
			width: drawWidth,
			height: drawHeight,
		};
	}

	const handleSetBackgroundFromUrl = useCallback(
		async ({ contractAddress, tokenIdHex, memeUrl }) => {
			// If memeUrl is provided, load directly
			if (memeUrl) {
				const cachedImg = getCachedImage(memeUrl);
				if (cachedImg) {
					// Instantly use it
					setBgImage(cachedImg);
					setUploadedImage(cachedImg);
					uploadedImageObj.current = cachedImg;

					// The rest of your "copy to originalBgDataRef" logic:
					const tempCanvas = document.createElement("canvas");
					tempCanvas.width = cachedImg.width;
					tempCanvas.height = cachedImg.height;
					const tCtx = tempCanvas.getContext("2d");
					tCtx.drawImage(cachedImg, 0, 0);
					originalBgDataRef.current = tempCanvas.toDataURL("image/png");

					drawCanvas();
					return;
				}
				try {
					const newImg = new Image();
					newImg.crossOrigin = "anonymous";
					newImg.src = memeUrl;

					newImg.onload = () => {
						storeCachedImage(memeUrl, newImg);

						// Update both so hasBackground will be true
						setBgImage(newImg);
						setUploadedImage(newImg);

						uploadedImageObj.current = newImg;

						const tempCanvas = document.createElement("canvas");
						tempCanvas.width = newImg.width;
						tempCanvas.height = newImg.height;
						const tCtx = tempCanvas.getContext("2d");
						tCtx.drawImage(newImg, 0, 0);
						originalBgDataRef.current = tempCanvas.toDataURL("image/png");

						drawCanvas();
					};

					newImg.onerror = (err) => {
						console.error("Failed to load meme image:", err);
					};
				} catch (err) {
					console.error("Error loading meme as background:", err);
				}
				return;
			}

			// Otherwise fetch from NFT
			try {
				const finalImageUrl = await fetchAlchemyMetadata({ contractAddress, tokenIdHex });

				const newImg = new Image();
				newImg.crossOrigin = "anonymous";
				newImg.src = finalImageUrl;

				newImg.onload = () => {
					// Update both so hasBackground will be true
					setBgImage(newImg);
					setUploadedImage(newImg);

					uploadedImageObj.current = newImg;

					const tempCanvas = document.createElement("canvas");
					tempCanvas.width = newImg.width;
					tempCanvas.height = newImg.height;
					const tCtx = tempCanvas.getContext("2d");
					tCtx.drawImage(newImg, 0, 0);

					originalBgDataRef.current = tempCanvas.toDataURL("image/png");
					drawCanvas();
				};

				newImg.onerror = (err) => {
					console.error("Failed to load NFT image:", err);
				};
			} catch (err) {
				console.error("Error in handleSetBackgroundFromUrl:", err);
			}
		},
		[drawCanvas]
	);

	const handleAddNftAsAsset = useCallback(
		async ({ contractAddress, tokenIdHex, memeUrl }) => {
			// If memeUrl is provided, load directly
			if (memeUrl) {
				try {
					const newImg = new Image();
					newImg.crossOrigin = "anonymous";
					newImg.src = memeUrl;

					newImg.onload = () => {
						const canvas = canvasRef.current;
						const imgAspect = newImg.width / newImg.height;
						const maxDim = 150;
						let targetWidth, targetHeight;

						if (imgAspect > 1) {
							targetWidth = maxDim;
							targetHeight = maxDim / imgAspect;
						} else {
							targetHeight = maxDim;
							targetWidth = maxDim * imgAspect;
						}

						// NEW: clamp inside bgRect if possible
						if (bgRect?.current && bgRect.current.width > 0 && bgRect.current.height > 0) {
							const r = bgRect.current;
							const scaleFactor = Math.min(r.width / targetWidth, r.height / targetHeight);
							if (scaleFactor < 1) {
								targetWidth *= scaleFactor;
								targetHeight *= scaleFactor;
							}
							var x = r.x + (r.width - targetWidth) / 2;
							var y = r.y + (r.height - targetHeight) / 2;
						} else {
							// fallback if no background
							var x = canvas.width / 2 - targetWidth / 2;
							var y = 20;
						}

						const newAssetLayer = {
							type: "asset",
							image: newImg,
							x: x,
							y: y,
							width: targetWidth,
							height: targetHeight,
							rotation: 0,
							isFlipped: false,
							isDragging: false,
							isResizing: false,
							isRotating: false,
							isSelected: true,
							dragOffsetX: 0,
							dragOffsetY: 0,
							rotationStart: 0,
							startAngle: 0,
							aspectRatio: targetWidth / targetHeight,
							lockAspectRatio: true,
						};

						layers.current.forEach((layer) => (layer.isSelected = false));
						layers.current.push(newAssetLayer);
						setIsAssetAdded(true);
						drawCanvas();
						updateAnySelected && updateAnySelected();
					};

					newImg.onerror = (err) => {
						console.error("Failed to load meme image for asset:", err);
					};
				} catch (err) {
					console.error("Error loading meme as asset:", err);
				}
				return;
			}

			// Otherwise fetch from NFT
			try {
				const finalImageUrl = await fetchAlchemyMetadata({ contractAddress, tokenIdHex });

				const newImg = new Image();
				newImg.crossOrigin = "anonymous";
				newImg.src = finalImageUrl;

				newImg.onload = () => {
					const canvas = canvasRef.current;
					const imgAspect = newImg.width / newImg.height;
					const maxDim = 150;
					let targetWidth, targetHeight;

					if (imgAspect > 1) {
						targetWidth = maxDim;
						targetHeight = maxDim / imgAspect;
					} else {
						targetHeight = maxDim;
						targetWidth = maxDim * imgAspect;
					}

					// -- NEW: If bgRect is valid, ensure we scale & place inside it --
					if (bgRect?.current && bgRect.current.width > 0 && bgRect.current.height > 0) {
						const r = bgRect.current;
						const scaleFactor = Math.min(r.width / targetWidth, r.height / targetHeight);
						if (scaleFactor < 1) {
							targetWidth *= scaleFactor;
							targetHeight *= scaleFactor;
						}
						var x = r.x + (r.width - targetWidth) / 2;
						var y = r.y + (r.height - targetHeight) / 2;
					} else {
						// fallback
						var x = canvas.width / 2 - targetWidth / 2;
						var y = 20;
					}

					const newAssetLayer = {
						type: "asset",
						image: newImg,
						x: x,
						y: y,
						width: targetWidth,
						height: targetHeight,
						rotation: 0,
						isFlipped: false,
						isDragging: false,
						isResizing: false,
						isRotating: false,
						isSelected: true,
						dragOffsetX: 0,
						dragOffsetY: 0,
						rotationStart: 0,
						startAngle: 0,
						aspectRatio: targetWidth / targetHeight,
						lockAspectRatio: true,
					};

					layers.current.forEach((layer) => (layer.isSelected = false));
					layers.current.push(newAssetLayer);
					setIsAssetAdded(true);

					drawCanvas();
					updateAnySelected && updateAnySelected();
				};

				newImg.onerror = (err) => {
					console.error("Failed to load NFT image for asset:", err);
				};
			} catch (err) {
				console.error("Error in handleAddNftAsAsset:", err);
			}
		},
		[drawCanvas, layers, setIsAssetAdded, updateAnySelected]
	);

	// ========== Asset Drawing & Selection ==========
	function drawAssetSelectionBox(ctx, asset) {
		// The center of the asset, so we can rotate about it
		const centerX = asset.x + asset.width / 2;
		const centerY = asset.y + asset.height / 2;

		ctx.save();
		ctx.translate(centerX, centerY);
		ctx.rotate((asset.rotation * Math.PI) / 180);

		// 1) Draw the dotted outline
		ctx.strokeStyle = "#836EF9";
		ctx.lineWidth = 3;
		ctx.setLineDash([5, 5]);
		ctx.strokeRect(-asset.width / 2, -asset.height / 2, asset.width, asset.height);
		ctx.setLineDash([]);

		// 2) Only show handles if not mobile

		const handleSize = 15;
		ctx.fillStyle = "#FFFFFF";
		ctx.strokeStyle = "#000000";
		ctx.lineWidth = 1;

		const halfW = asset.width / 2;
		const halfH = asset.height / 2;

		// A) If lockAspectRatio => only corners
		let handlePositions;
		if (asset.lockAspectRatio) {
			handlePositions = [
				{ name: "top-left", x: -halfW, y: -halfH },
				{ name: "top-right", x: halfW, y: -halfH },
				{ name: "bottom-right", x: halfW, y: halfH },
				{ name: "bottom-left", x: -halfW, y: halfH },
			];
		} else {
			// B) Otherwise => all corners + midpoints
			handlePositions = [
				{ name: "top-left", x: -halfW, y: -halfH },
				{ name: "top-center", x: 0, y: -halfH },
				{ name: "top-right", x: halfW, y: -halfH },
				{ name: "right-center", x: halfW, y: 0 },
				{ name: "bottom-right", x: halfW, y: halfH },
				{ name: "bottom-center", x: 0, y: halfH },
				{ name: "bottom-left", x: -halfW, y: halfH },
				{ name: "left-center", x: -halfW, y: 0 },
			];
		}

		// Draw each handle
		handlePositions.forEach((pos) => {
			const hx = pos.x - handleSize / 2;
			const hy = pos.y - handleSize / 2;
			ctx.fillRect(hx, hy, handleSize, handleSize);
			ctx.strokeRect(hx, hy, handleSize, handleSize);
		});

		// 3) Rotation handle (unchanged)
		const handleRadius = 10;
		const rotationHandleY = halfH + 20; // push it below the bottom edge
		ctx.beginPath();
		ctx.arc(0, rotationHandleY, handleRadius, 0, 2 * Math.PI);
		ctx.fill();
		ctx.stroke();

		ctx.beginPath();
		ctx.moveTo(0, halfH);
		ctx.lineTo(0, rotationHandleY - handleRadius);
		ctx.stroke();

		ctx.restore();
		if (asset.isRotating) {
			ctx.save();
			ctx.fillStyle = "rgba(0,0,0,0.9)";
			ctx.fillRect(centerX - 20, centerY - 15, 40, 25); // small background rect so text is readable

			ctx.font = "14px sans-serif";
			ctx.fillStyle = "#fff";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";

			// Round the angle so it’s an integer
			const angleStr = `${Math.round(asset.rotation)}°`;
			ctx.fillText(angleStr, centerX, centerY - 2);

			ctx.restore();
		}
	}

	// ========== MULTILINE TEXT Drawing & Selection ==========

	function drawTextItem(ctx, layer, hideSelection) {
		const {
			x,
			y,
			rotation,
			fontSize,
			color,
			text,
			isSelected,
			fontFamily,
			isBold,
			isItalic,
			isUnderlined,
			isStrikethrough,
			isAllCaps,
			opacity,
		} = layer;

		ctx.save();
		ctx.translate(x, y);
		ctx.rotate((rotation * Math.PI) / 180);

		// Build font
		let fontDesc = "";
		if (isItalic) fontDesc += "italic ";
		if (isBold) fontDesc += "bold ";
		fontDesc += `${fontSize}px ${fontFamily || "Impact"}`;
		ctx.font = fontDesc;

		ctx.textAlign = "left"; // We'll manually center by offsets
		ctx.textBaseline = "alphabetic"; // so 0,y is the bottom of each line
		ctx.globalAlpha = opacity ?? 1.0;

		// Possibly uppercase
		const displayText = isAllCaps ? text.toUpperCase() : text;

		// Outline color
		const outlineColor = color === "#ffffff" ? "#000000" : "#ffffff";

		// Shadow, outline
		ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
		ctx.shadowBlur = 4;
		ctx.shadowOffsetX = 2;
		ctx.shadowOffsetY = 2;
		ctx.lineWidth = Math.ceil(fontSize * 0.07);
		ctx.strokeStyle = outlineColor;
		ctx.lineJoin = "round";
		ctx.miterLimit = 2;

		// Use the new function that returns lineMetrics
		const canvas = canvasRef.current;
		const { lineMetrics, boundingBoxW, boundingBoxH } = getMultilineTextMetrics(
			layer,
			ctx,
			bgRect.current
		);

		// We'll treat the entire block as centered around (0,0).
		// So the top-left corner is (-boundingBoxW/2, -boundingBoxH/2).
		// But we want to line-draw from that top corner going down.
		let currentY = -boundingBoxH / 2;

		for (const metric of lineMetrics) {
			const { text: lineStr, width, ascent, descent, lineHeight } = metric;

			// The top of this line is currentY.
			// We want to place the baseline at currentY + ascent.
			const baselineY = currentY + ascent;

			// We'll center horizontally => left X = -width/2
			const leftX = -width / 2;

			// stroke + fill
			ctx.strokeText(lineStr, leftX, baselineY);
			ctx.fillStyle = color;
			ctx.fillText(lineStr, leftX, baselineY);

			// If underlined/strikethrough:
			if (isUnderlined || isStrikethrough) {
				// measureText again or use `width`
				const halfW = width / 2;

				// Underline => typically near baseline
				if (isUnderlined) {
					// for example, 2 px below baseline
					let underlineY = baselineY + 2;
					ctx.beginPath();
					ctx.moveTo(-halfW, underlineY);
					ctx.lineTo(halfW, underlineY);
					ctx.lineWidth = Math.max(2, fontSize * 0.03);
					ctx.strokeStyle = color;
					ctx.stroke();
				}

				// Strikethrough => halfway between top and baseline
				if (isStrikethrough) {
					let strikeY = baselineY - ascent / 2;
					ctx.beginPath();
					ctx.moveTo(-halfW, strikeY);
					ctx.lineTo(halfW, strikeY);
					ctx.lineWidth = Math.max(2, fontSize * 0.06);
					ctx.strokeStyle = color;
					ctx.stroke();
				}
			}

			// Move down for the next line
			currentY += lineHeight;
		}

		ctx.restore();

		// If selected, draw selection bounding box
		if (isSelected && !hideSelection) {
			drawTextSelectionBox(ctx, layer, boundingBoxW, boundingBoxH);
		}
	}

	function drawTextSelectionBox(ctx, item, boxW, boxH) {
		const { x, y, rotation } = item;

		ctx.save();
		ctx.translate(x, y);
		ctx.rotate((rotation * Math.PI) / 180);

		// If you prefer, you can re-do getMultilineTextMetrics, but let's accept them:
		const boundingBoxW = boxW;
		const boundingBoxH = boxH;

		ctx.strokeStyle = "#836EF9";
		ctx.lineWidth = 3;
		ctx.setLineDash([5, 5]);

		// Draw the rectangle from (-W/2, -H/2) to (W/2, H/2)
		ctx.strokeRect(-boundingBoxW / 2, -boundingBoxH / 2, boundingBoxW, boundingBoxH);
		ctx.setLineDash([]);

		const handleSize = 15;
		ctx.fillStyle = "#FFFFFF";
		ctx.strokeStyle = "#000000";
		ctx.lineWidth = 1;

		// bottom-right corner => resize handle
		const cornerX = boundingBoxW / 2 - handleSize / 2;
		const cornerY = boundingBoxH / 2 - handleSize / 2;

		ctx.fillRect(cornerX, cornerY, handleSize, handleSize);
		ctx.strokeRect(cornerX, cornerY, handleSize, handleSize);

		// rotation handle => below bottom
		const handleRadius = 10;
		const rotationHandleY = boundingBoxH / 2 + 20; // offset 20 px below
		ctx.beginPath();
		ctx.arc(0, rotationHandleY, handleRadius, 0, 2 * Math.PI);
		ctx.fill();
		ctx.stroke();

		// connecting line
		ctx.beginPath();
		ctx.moveTo(0, boundingBoxH / 2);
		ctx.lineTo(0, rotationHandleY - handleRadius);
		ctx.stroke();

		ctx.restore();
		if (item.isRotating) {
			// 2) Draw angle label upright
			ctx.save();

			ctx.fillStyle = "rgba(0,0,0,0.9)";
			ctx.fillRect(x - 20, y - 15, 40, 25);

			ctx.font = "14px sans-serif";
			ctx.fillStyle = "#fff";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillText(`${Math.round(rotation)}°`, x, y - 2);

			ctx.restore();
		}
	}

	// ========== Hit-tests for TEXT (multi-line) ==========
	// Standard getEventPosition
	const getEventPosition = useCallback((e) => {
		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		let x, y;

		if (e.touches && e.touches.length > 0) {
			const touch = e.touches[0];
			x = touch.clientX - rect.left;
			y = touch.clientY - rect.top;
		} else if (e.changedTouches && e.changedTouches.length > 0) {
			const touch = e.changedTouches[0];
			x = touch.clientX - rect.left;
			y = touch.clientY - rect.top;
		} else {
			x = e.clientX - rect.left;
			y = e.clientY - rect.top;
		}
		return { x, y };
	}, []);

	// Helper to get position from a specific Touch object
	const getTouchPosition = useCallback((touch) => {
		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		const x = touch.clientX - rect.left;
		const y = touch.clientY - rect.top;
		return { x, y };
	}, []);

	function isMouseOnText(mouseX, mouseY, item) {
		const { x, y, rotation } = item;
		const dx = mouseX - x;
		const dy = mouseY - y;
		const angle = (-rotation * Math.PI) / 180;
		// rotate the mouse point in the opposite direction
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		// measure bounding box
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(item, ctx, bgRect.current);

		// check if rotatedX/Y are inside [-W/2, W/2] x [-H/2, H/2]
		const halfW = boundingBoxW / 2;
		const halfH = boundingBoxH / 2;
		return rotatedX >= -halfW && rotatedX <= halfW && rotatedY >= -halfH && rotatedY <= halfH;
	}

	function isMouseOnTextResizeHandle(mouseX, mouseY, item) {
		const { x, y, rotation } = item;
		const dx = mouseX - x;
		const dy = mouseY - y;
		const angle = (-rotation * Math.PI) / 180;
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(item, ctx, bgRect.current);

		const handleSize = 15;
		const handleX = boundingBoxW / 2 - handleSize / 2;
		const handleY = boundingBoxH / 2 - handleSize / 2;

		return (
			rotatedX >= handleX &&
			rotatedX <= handleX + handleSize &&
			rotatedY >= handleY &&
			rotatedY <= handleY + handleSize
		);
	}

	function isMouseOnTextRotationHandle(mouseX, mouseY, item) {
		const { x, y, rotation } = item;

		// Position of mouse relative to item center:
		const dx = mouseX - x;
		const dy = mouseY - y;
		const angle = (-rotation * Math.PI) / 180;
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		// We measure bounding-box for text
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const { boundingBoxH } = getMultilineTextMetrics(item, ctx, bgRect.current);

		// Our new handle is at bottom + 20
		const handleRadius = 10;
		const rotationHandleY = boundingBoxH / 2 + 20;

		// Distance from that point
		const dist = Math.hypot(rotatedX, rotatedY - rotationHandleY);
		return dist <= handleRadius;
	}

	// ========== Hit-tests for ASSETS ==========

	const isMouseOnAsset = (x, y, asset) => {
		const centerX = asset.x + asset.width / 2;
		const centerY = asset.y + asset.height / 2;
		const dx = x - centerX;
		const dy = y - centerY;
		const angle = (-asset.rotation * Math.PI) / 180;
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		return (
			rotatedX >= -asset.width / 2 &&
			rotatedX <= asset.width / 2 &&
			rotatedY >= -asset.height / 2 &&
			rotatedY <= asset.height / 2
		);
	};

	function isMouseOnAssetResizeHandle(mx, my, asset) {
		const handleSize = 15;
		// center of asset:
		const centerX = asset.x + asset.width / 2;
		const centerY = asset.y + asset.height / 2;
		// mouse relative coords:
		const dx = mx - centerX;
		const dy = my - centerY;
		// rotate them:
		const angle = (-asset.rotation * Math.PI) / 180;
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		const halfW = asset.width / 2;
		const halfH = asset.height / 2;

		let handlePositions;
		if (asset.lockAspectRatio) {
			handlePositions = [
				{ name: "top-left", x: -halfW, y: -halfH },
				{ name: "top-right", x: halfW, y: -halfH },
				{ name: "bottom-right", x: halfW, y: halfH },
				{ name: "bottom-left", x: -halfW, y: halfH },
			];
		} else {
			handlePositions = [
				{ name: "top-left", x: -halfW, y: -halfH },
				{ name: "top-center", x: 0, y: -halfH },
				{ name: "top-right", x: halfW, y: -halfH },
				{ name: "right-center", x: halfW, y: 0 },
				{ name: "bottom-right", x: halfW, y: halfH },
				{ name: "bottom-center", x: 0, y: halfH },
				{ name: "bottom-left", x: -halfW, y: halfH },
				{ name: "left-center", x: -halfW, y: 0 },
			];
		}

		for (const pos of handlePositions) {
			const hx = pos.x - handleSize / 2;
			const hy = pos.y - handleSize / 2;
			if (
				rotatedX >= hx &&
				rotatedX <= hx + handleSize &&
				rotatedY >= hy &&
				rotatedY <= hy + handleSize
			) {
				return { hit: true, handleName: pos.name };
			}
		}
		return { hit: false };
	}

	function isMouseOnAssetRotationHandle(x, y, asset) {
		const handleRadius = 10;

		// The center of the asset is:
		const centerX = asset.x + asset.width / 2;
		const centerY = asset.y + asset.height / 2;

		// Translate mouse coords relative to center, then rotate them "back"
		const dx = x - centerX;
		const dy = y - centerY;
		const angle = (-asset.rotation * Math.PI) / 180;
		const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
		const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

		// Now we check distance from the new rotation handle location:
		// Instead of ( -asset.height/2 - 30 ), use ( +asset.height/2 + 20 )
		const rotationHandleY = asset.height / 2 + 20;

		// Check if the mouse is within `handleRadius` of that point
		const distance = Math.hypot(rotatedX, rotatedY - rotationHandleY);
		return distance <= handleRadius;
	}

	// ========== Mouse Events ==========

	const multiTouchState = useRef({
		isPinching: false,
		initialDistance: 0,
		initialAngle: 0,
		initialRotation: 0,
		initialWidth: 0,
		initialHeight: 0,
		initialFontSize: 0,
		centerX: 0,
		centerY: 0,
		selectedLayerId: null, // we can store index or a reference
	});

	// Helper to find the distance and angle between two touches
	const getDistanceAndAngle = (p1, p2) => {
		const dx = p2.x - p1.x;
		const dy = p2.y - p1.y;
		const distance = Math.hypot(dx, dy);
		const angle = Math.atan2(dy, dx) * (180 / Math.PI);
		return { distance, angle };
	};

	// We can try to find which layer is currently selected.
	// If none is selected, pinch won't do anything.
	const getSelectedLayer = () => layers.current.find((l) => l.isSelected);

	const handleMouseDown = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
				e.stopPropagation();
			}
			const { x, y } = getEventPosition(e);

			// Eraser?
			if (isErasing) {
				isMouseDownInEraseMode.current = true;
				eraserStrokesRef.current.push({ path: [{ x, y }], size: eraserSize });
				drawCanvas();
				return;
			}

			// Deselect everything
			layers.current.forEach((layer) => {
				layer.isSelected = false;
				layer.isResizing = false;
				layer.isRotating = false;
				layer.isDragging = false;
			});

			// Desktop handle logic
			for (let i = layers.current.length - 1; i >= 0; i--) {
				const layer = layers.current[i];
				if (layer.type === "asset") {
					const hitTest = isMouseOnAssetResizeHandle(x, y, layer);
					if (hitTest.hit) {
						layer.isResizing = true;
						layer.isSelected = true;
						layer.activeHandle = hitTest.handleName;
						if (layer.lockAspectRatio) {
							// Only do this for corner handles:
							const cornerHandles = ["top-left", "top-right", "bottom-left", "bottom-right"];
							if (cornerHandles.includes(hitTest.handleName)) {
								// figure out the anchor corner for that handle
								// e.g. if top-left, anchor corner is (oldX+oldW, oldY+oldH)

								const oldX = layer.x;
								const oldY = layer.y;
								const oldW = layer.width;
								const oldH = layer.height;

								let anchorX, anchorY;
								if (hitTest.handleName === "top-left") {
									// anchor = bottom-right
									anchorX = oldX + oldW;
									anchorY = oldY + oldH;
								} else if (hitTest.handleName === "top-right") {
									// anchor = bottom-left
									anchorX = oldX;
									anchorY = oldY + oldH;
								} else if (hitTest.handleName === "bottom-left") {
									// anchor = top-right
									anchorX = oldX + oldW;
									anchorY = oldY;
								} else {
									// bottom-right => anchor = top-left
									anchorX = oldX;
									anchorY = oldY;
								}

								// Then measure the difference from anchor to the mouseDown point:
								const diffX = Math.abs(x - anchorX);
								const diffY = Math.abs(y - anchorY);

								// whichever is bigger = driver
								if (diffX > diffY) {
									layer.aspectDriver = "width";
								} else {
									layer.aspectDriver = "height";
								}
							}
						}
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					} else if (isMouseOnAssetRotationHandle(x, y, layer)) {
						layer.isRotating = true;
						layer.isSelected = true;
						layer.rotationStart = layer.rotation;
						layer.startAngle =
							Math.atan2(y - (layer.y + layer.height / 2), x - (layer.x + layer.width / 2)) *
							(180 / Math.PI);
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					} else if (isMouseOnAsset(x, y, layer)) {
						layer.isDragging = true;
						layer.isSelected = true;
						layer.dragOffsetX = x - layer.x;
						layer.dragOffsetY = y - layer.y;
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					}
				} else if (layer.type === "text") {
					if (!isMobile && isMouseOnTextResizeHandle(x, y, layer)) {
						layer.isResizing = true;
						layer.isSelected = true;
						const canvas = canvasRef.current;
						const ctx = canvas.getContext("2d");
						const { boundingBoxW } = getMultilineTextMetrics(layer, ctx, bgRect.current);
						layer._initialBoundingBoxW = boundingBoxW;
						layer._initialFontSize = layer.fontSize;
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					} else if (!isMobile && isMouseOnTextRotationHandle(x, y, layer)) {
						layer.isRotating = true;
						layer.isSelected = true;
						layer.rotationStart = layer.rotation;
						layer.startAngle = Math.atan2(y - layer.y, x - layer.x) * (180 / Math.PI);
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					} else if (isMouseOnText(x, y, layer)) {
						layer.isDragging = true;
						layer.isSelected = true;
						layer.dragOffsetX = x - layer.x;
						layer.dragOffsetY = y - layer.y;
						// layers.current.push(...layers.current.splice(i, 1));
						break;
					}
				}
			}

			// If text layer is selected, sync controls
			const selectedTextLayer = layers.current.find((l) => l.isSelected && l.type === "text");
			if (selectedTextLayer) {
				setSelectedFontFamily(selectedTextLayer.fontFamily || "Impact");
				setIsBold(!!selectedTextLayer.isBold);
				setIsItalic(!!selectedTextLayer.isItalic);
				setIsUnderlined(!!selectedTextLayer.isUnderlined);
				setIsStrikethrough(!!selectedTextLayer.isStrikethrough);
				setIsAllCaps(!!selectedTextLayer.isAllCaps);
				setTextOpacity(selectedTextLayer.opacity ?? 1.0);
			}

			drawCanvas();
			updateSelectedTextColorToggle();
		},
		[getEventPosition, isErasing, eraserSize, drawCanvas]
	);

	const handleMouseMove = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
			}
			const { x, y } = getEventPosition(e);

			// Eraser
			if (isErasing && isMouseDownInEraseMode.current) {
				const strokeObj = eraserStrokesRef.current[eraserStrokesRef.current.length - 1];
				strokeObj.path.push({ x, y });
				drawCanvas();
				return;
			}

			// Move/resize/rotate
			layers.current.forEach((layer) => {
				if (layer.type === "asset") {
					if (layer.isDragging) {
						let newLeft = x - layer.dragOffsetX;
						let newTop = y - layer.dragOffsetY;

						const r = bgRect.current; // background rectangle
						const assetW = layer.width;
						const assetH = layer.height;
						const partialMargin = 20; // "a little padding"

						// Clamp horizontally:
						// min X => asset's right edge can be at BG’s left edge + partialMargin
						// max X => asset's left edge  can be at BG’s right edge - partialMargin
						newLeft = Math.max(newLeft, r.x + partialMargin - assetW);
						newLeft = Math.min(newLeft, r.x + r.width - partialMargin);

						// Clamp vertically:
						// min Y => asset's bottom edge can be at BG’s top edge + partialMargin
						// max Y => asset's top edge    can be at BG’s bottom edge - partialMargin
						newTop = Math.max(newTop, r.y + partialMargin - assetH);
						newTop = Math.min(newTop, r.y + r.height - partialMargin);

						// Assign clamped coords back to the layer
						layer.x = newLeft;
						layer.y = newTop;
					} else if (layer.isResizing) {
						// Current mouse (x, y)
						const mx = x;
						const my = y;

						// Store old values for convenience:
						const oldX = layer.x;
						const oldY = layer.y;
						const oldW = layer.width;
						const oldH = layer.height;

						// We'll figure out newX, newY, newW, newH
						let newX = oldX;
						let newY = oldY;
						let newW = oldW;
						let newH = oldH;

						// Which handle did we click?
						switch (layer.activeHandle) {
							// ========== CORNERS ==========
							case "top-left": {
								const oldRight = oldX + oldW;
								const oldBottom = oldY + oldH;

								newX = mx; // mouse is the new left
								newY = my; // mouse is the new top
								newW = oldRight - newX;
								newH = oldBottom - newY;
								break;
							}
							case "top-right": {
								const oldLeft = oldX;
								const oldBottom = oldY + oldH;

								const newRight = mx;
								newY = my; // top
								newW = newRight - oldLeft;
								newH = oldBottom - newY;
								break;
							}
							case "bottom-left": {
								const oldTop = oldY;
								const oldRight = oldX + oldW;

								newX = mx; // left
								const newBottom = my;
								newW = oldRight - newX;
								newH = newBottom - oldTop;
								break;
							}
							case "bottom-right": {
								// anchor top-left
								const newRight = mx;
								const newBottom = my;
								newW = newRight - oldX;
								newH = newBottom - oldY;
								break;
							}

							// ========== SIDES ==========
							case "top-center": {
								// anchor bottom
								const oldBottom = oldY + oldH;
								newY = my;
								newH = oldBottom - newY;
								// Keep X, W as is
								newX = oldX;
								newW = oldW;
								break;
							}
							case "bottom-center": {
								// anchor top
								const oldTop = oldY;
								const newBottom = my;
								newH = newBottom - oldTop;
								newY = oldTop;
								newX = oldX;
								newW = oldW;
								break;
							}
							case "left-center": {
								// anchor right
								const oldRight = oldX + oldW;
								newX = mx;
								newW = oldRight - newX;
								// keep Y, H
								newY = oldY;
								newH = oldH;
								break;
							}
							case "right-center": {
								// anchor left
								const oldLeft = oldX;
								const newRight = mx;
								newW = newRight - oldLeft;
								newX = oldLeft;
								newY = oldY;
								newH = oldH;
								break;
							}

							default:
								// if something else, do nothing
								break;
						} // end switch

						// ============================
						// 1) Prevent negative or too-small
						// ============================
						if (newW < 10) newW = 10;
						if (newH < 10) newH = 10;

						// ============================
						// 2) If lockAspectRatio, handle corners vs sides
						// ============================
						if (layer.lockAspectRatio) {
							const oldRatio = oldW / oldH;
							// Only apply aspect ratio for corners:
							const cornerHandles = ["top-left", "top-right", "bottom-left", "bottom-right"];
							if (cornerHandles.includes(layer.activeHandle)) {
								// 1) Instead of re-checking each frame, use `layer.aspectDriver`
								//    set on mousedown. (See notes below.)
								//    If it’s not set, you can fallback to the old approach.
								if (!layer.aspectDriver) {
									// fallback: figure out which dimension changed more for the very first time
									const widthDiff = Math.abs(newW - oldW);
									const heightDiff = Math.abs(newH - oldH);
									if (widthDiff > heightDiff) {
										// driver = width
										newH = newW / oldRatio;
										layer.aspectDriver = "width";
									} else {
										// driver = height
										newW = newH * oldRatio;
										layer.aspectDriver = "height";
									}
								} else {
									// We already know which axis is the driver:
									if (layer.aspectDriver === "width") {
										// newW stays, compute newH
										newH = newW / oldRatio;
									} else {
										// driver is height, so newH stays, compute newW
										newW = newH * oldRatio;
									}
								}

								// 2) Recompute the anchor corner so we keep the correct corner pinned
								switch (layer.activeHandle) {
									case "top-left": {
										const oldRight = oldX + oldW;
										const oldBottom = oldY + oldH;
										// new left => oldRight - newW
										// new top  => oldBottom - newH
										newX = oldRight - newW;
										newY = oldBottom - newH;
										break;
									}
									case "top-right": {
										const oldLeft = oldX;
										const oldBottom = oldY + oldH;
										newY = oldBottom - newH;
										newX = oldLeft;
										// effectively, bottom-left is pinned
										break;
									}
									case "bottom-left": {
										const oldTop = oldY;
										const oldRight = oldX + oldW;
										newX = oldRight - newW;
										newY = oldTop;
										break;
									}
									case "bottom-right": {
										// top-left pinned
										newX = oldX;
										newY = oldY;
										break;
									}
									default:
										break;
								}
							}
							// If it's a side handle, we do nothing special here
							// (or you can add your own logic).
						}

						// ============================
						// 3) Assign them back
						// ============================
						layer.x = newX;
						layer.y = newY;
						layer.width = newW;
						layer.height = newH;

						// ============================
						// 4) **Ratio-Preserving Clamp** to background
						// ============================
						const r = bgRect.current;

						// We'll see if the final bounding box is outside the background.
						// If lockAspectRatio is ON and it overflows, we do a scale so it fits back in.
						// Otherwise, do the naive clamp for non-ratio or partial cases.

						const finalRight = layer.x + layer.width;
						const finalBottom = layer.y + layer.height;

						const overflowLeft = r.x - layer.x; // positive if left is beyond
						const overflowRight = finalRight - (r.x + r.width); // positive if right is beyond
						const overflowTop = r.y - layer.y; // positive if top is beyond
						const overflowBottom = finalBottom - (r.y + r.height); // positive if bottom is beyond

						const isOverflowing =
							overflowLeft > 0 || overflowRight > 0 || overflowTop > 0 || overflowBottom > 0;

						if (layer.lockAspectRatio && isOverflowing) {
							// Figure out the pinned corner again:
							// e.g. if activeHandle is top-left, pinned corner is bottom-right, etc.
							// We'll store that anchor, then scale the width/height so it fits.

							let anchorX = null;
							let anchorY = null;
							switch (layer.activeHandle) {
								case "top-left":
									anchorX = oldX + oldW; // bottom-right corner
									anchorY = oldY + oldH;
									break;
								case "top-right":
									anchorX = oldX; // bottom-left corner
									anchorY = oldY + oldH;
									break;
								case "bottom-left":
									anchorX = oldX + oldW; // top-right corner
									anchorY = oldY;
									break;
								case "bottom-right":
									anchorX = oldX; // top-left corner
									anchorY = oldY;
									break;
								default:
									// If user grabbed a side handle or we can’t figure out anchor:
									// do a simpler approach or just revert to naive clamp
									anchorX = null;
							}

							if (anchorX != null) {
								// We'll measure how far we're overflowing on each side,
								// figure out the scale needed to bring it inside.
								// E.g. if overflowRight > 0, we can see how much width must shrink
								// so that finalRight = r.x + r.width
								// scale = (currentWidth - overflowRight) / currentWidth
								// We'll do that for each side, pick the smallest scale that resolves all overflows.

								let scaleX = 1.0;
								let scaleY = 1.0;

								if (overflowLeft > 0) {
									// We need the left edge to be r.x
									const currentWidth = layer.width;
									const neededWidth = currentWidth - overflowLeft;
									// scale factor:
									scaleX = Math.min(scaleX, neededWidth / currentWidth);
								}
								if (overflowRight > 0) {
									const currentWidth = layer.width;
									const neededWidth = currentWidth - overflowRight;
									scaleX = Math.min(scaleX, neededWidth / currentWidth);
								}
								if (overflowTop > 0) {
									const currentHeight = layer.height;
									const neededHeight = currentHeight - overflowTop;
									scaleY = Math.min(scaleY, neededHeight / currentHeight);
								}
								if (overflowBottom > 0) {
									const currentHeight = layer.height;
									const neededHeight = currentHeight - overflowBottom;
									scaleY = Math.min(scaleY, neededHeight / currentHeight);
								}

								// final scale = min(scaleX, scaleY)
								const finalScale = Math.min(scaleX, scaleY);

								if (finalScale < 1) {
									// apply it:
									const newScaledW = layer.width * finalScale;
									const newScaledH = layer.height * finalScale;

									// now re-pin to anchor
									if (layer.activeHandle === "top-left") {
										// anchor is bottom-right => x = anchorX - newScaledW
										layer.x = anchorX - newScaledW;
										layer.y = anchorY - newScaledH;
									} else if (layer.activeHandle === "top-right") {
										layer.x = anchorX;
										layer.y = anchorY - newScaledH;
									} else if (layer.activeHandle === "bottom-left") {
										layer.x = anchorX - newScaledW;
										layer.y = anchorY;
									} else if (layer.activeHandle === "bottom-right") {
										layer.x = anchorX;
										layer.y = anchorY;
									}

									layer.width = newScaledW;
									layer.height = newScaledH;
								}
							} else {
								// Fallback: if no corner anchor, do the naive clamp
								if (layer.x < r.x) {
									layer.x = r.x;
								}
								if (layer.y < r.y) {
									layer.y = r.y;
								}
								if (layer.x + layer.width > r.x + r.width) {
									layer.width = r.x + r.width - layer.x;
								}
								if (layer.y + layer.height > r.y + r.height) {
									layer.height = r.y + r.height - layer.y;
								}
							}
						} else if (!layer.lockAspectRatio) {
							// For non-ratio or not overflowing, do the original naive clamp:
							if (layer.x < r.x) {
								layer.x = r.x;
							}
							if (layer.y < r.y) {
								layer.y = r.y;
							}
							if (layer.x + layer.width > r.x + r.width) {
								layer.width = r.x + r.width - layer.x;
							}
							if (layer.y + layer.height > r.y + r.height) {
								layer.height = r.y + r.height - layer.y;
							}
						}
					} else if (layer.isRotating) {
						const centerX = layer.x + layer.width / 2;
						const centerY = layer.y + layer.height / 2;
						const dx = x - centerX;
						const dy = y - centerY;
						// 1) Compute raw rotation
						const currentAngle = Math.atan2(dy, dx) * (180 / Math.PI);
						const angleDifference = currentAngle - layer.startAngle;
						const rawRotation = layer.rotationStart + angleDifference;

						// 2) Possibly snap
						layer.rotation = maybeSnapAngle(rawRotation, 45, 5);
						layer.rotation = (layer.rotation + 360) % 360;
					}
				} else if (layer.type === "text") {
					if (layer.isDragging) {
						const newX = x - layer.dragOffsetX;
						const newY = y - layer.dragOffsetY;

						// measure text bounding box:
						const canvas = canvasRef.current;
						const ctx = canvas.getContext("2d");
						const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(
							layer,
							ctx,
							bgRect.current
						);

						// The half-sizes for text
						const halfW = boundingBoxW / 2;
						const halfH = boundingBoxH / 2;

						// The background rect
						const r = bgRect.current;

						// clamp text center to the background rect
						let clampedX = Math.max(r.x + halfW, Math.min(r.x + r.width - halfW, newX));
						let clampedY = Math.max(r.y + halfH, Math.min(r.y + r.height - halfH, newY));

						layer.x = clampedX;
						layer.y = clampedY;
					} else if (layer.isResizing) {
						// 1) Compute ratio => new fontSize (same as before)
						const centerX = layer.x;
						const centerY = layer.y;
						const dx = x - centerX;
						const dy = y - centerY;
						const angle = (-layer.rotation * Math.PI) / 180;
						const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);

						if (typeof layer._initialBoundingBoxW !== "number") return;

						const ratio = (rotatedX * 2) / layer._initialBoundingBoxW;
						const dampingFactor = 0.8;
						const scale = 1 + (ratio - 1) * dampingFactor;
						const newFontSize = Math.max(layer._initialFontSize * scale, 10);

						layer.fontSize = newFontSize;

						// 2) Measure bounding box for the new font
						const canvas = canvasRef.current;
						const ctx = canvas.getContext("2d");
						const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(
							layer,
							ctx,
							bgRect.current
						);

						// 3) Check if bounding box bigger than background
						const bg = bgRect.current; // your background rect { x, y, width, height }

						// We'll store a finalFontSize that we might reduce if it overflows
						let finalFontSize = layer.fontSize;
						let needsRecalc = false;

						// if boundingBoxW > bg.width, we need to shrink further
						if (boundingBoxW > bg.width) {
							// compute how much we need to scale down
							const scaleDown = bg.width / boundingBoxW;
							finalFontSize = finalFontSize * scaleDown;
							needsRecalc = true;
						}

						// if boundingBoxH > bg.height, shrink further
						if (boundingBoxH > bg.height) {
							const scaleDown = bg.height / boundingBoxH;
							finalFontSize = finalFontSize * scaleDown;
							needsRecalc = true;
						}

						// 4) If we changed finalFontSize, reassign & optionally re-measure bounding box
						if (needsRecalc) {
							layer.fontSize = Math.max(finalFontSize, 10);

							// Optionally measure bounding box again if you want super-accurate results
							// const { boundingBoxW: w2, boundingBoxH: h2 } = getMultilineTextMetrics(layer, ctx, canvas);
						}

						// 5) (Optional) clamp position so the text bounding box does not go outside bgRect
						//    - measure again with the updated font size
						const { boundingBoxW: w2, boundingBoxH: h2 } = getMultilineTextMetrics(
							layer,
							ctx,
							bgRect.current
						);

						// The text is centered at (layer.x, layer.y), so the top-left is:
						let textLeft = layer.x - w2 / 2;
						let textTop = layer.y - h2 / 2;
						let textRight = textLeft + w2;
						let textBottom = textTop + h2;

						// If it goes beyond the background, clamp
						if (textLeft < bg.x) {
							layer.x = bg.x + w2 / 2;
						}
						if (textTop < bg.y) {
							layer.y = bg.y + h2 / 2;
						}
						if (textRight > bg.x + bg.width) {
							layer.x = bg.x + bg.width - w2 / 2;
						}
						if (textBottom > bg.y + bg.height) {
							layer.y = bg.y + bg.height - h2 / 2;
						}
					} else if (layer.isRotating) {
						const dx = x - layer.x;
						const dy = y - layer.y;
						// 1) Compute raw rotation
						const currentAngle = Math.atan2(dy, dx) * (180 / Math.PI);
						const angleDifference = currentAngle - layer.startAngle;
						const rawRotation = layer.rotationStart + angleDifference;

						// 2) Possibly snap
						layer.rotation = maybeSnapAngle(rawRotation, 45, 5);
						layer.rotation = (layer.rotation + 360) % 360;
					}
				}
			});

			drawCanvas();
		},
		[getEventPosition, isErasing, drawCanvas]
	);

	const handleMouseUp = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
			}
			if (isErasing) {
				isMouseDownInEraseMode.current = false;
				return;
			}
			layers.current.forEach((layer) => {
				layer.isDragging = false;
				layer.isResizing = false;
				layer.isRotating = false;
			});
		},
		[isErasing]
	);

	// ========== Touch Events ==========

	const handleTouchStart = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
				e.stopPropagation();
			}

			const touches = e.touches;
			if (touches.length === 1) {
				// single-finger => normal logic
				handleMouseDown(e);
			} else if (touches.length === 2 && isMobile) {
				// Two-finger pinch
				const layer = getSelectedLayer();
				if (!layer) {
					// If no layer is selected, pinch won't do anything
					return;
				}
				multiTouchState.current.isPinching = true;

				// measure initial distance/angle
				const t1 = touches[0];
				const t2 = touches[1];
				const p1 = getTouchPosition(t1);
				const p2 = getTouchPosition(t2);
				const { distance, angle } = getDistanceAndAngle(p1, p2);

				multiTouchState.current.initialDistance = distance;
				multiTouchState.current.initialAngle = angle;
				multiTouchState.current.initialRotation = layer.rotation || 0;

				if (layer.type === "asset") {
					multiTouchState.current.initialWidth = layer.width;
					multiTouchState.current.initialHeight = layer.height;
					// find the center
					multiTouchState.current.centerX = layer.x + layer.width / 2;
					multiTouchState.current.centerY = layer.y + layer.height / 2;
				} else if (layer.type === "text") {
					// We'll treat it like we do with resizing the bounding box
					// measure bounding box:
					const canvas = canvasRef.current;
					const ctx = canvas.getContext("2d");
					const { boundingBoxW, boundingBoxH } = getMultilineTextMetrics(
						layer,
						ctx,
						bgRect.current
					);
					multiTouchState.current.initialFontSize = layer.fontSize;
					multiTouchState.current.centerX = layer.x;
					multiTouchState.current.centerY = layer.y;
					multiTouchState.current.initialWidth = boundingBoxW; // Not used for text, but can store
					multiTouchState.current.initialHeight = boundingBoxH;
				}
			}
		},
		[handleMouseDown, isMobile, getTouchPosition]
	);

	const handleTouchMove = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
			}
			const touches = e.touches;

			if (touches.length === 1 && !multiTouchState.current.isPinching) {
				// single-finger drag
				handleMouseMove(e);
			} else if (touches.length === 2 && multiTouchState.current.isPinching) {
				const layer = getSelectedLayer();
				if (!layer) return;

				// measure new distance, angle
				const t1 = touches[0];
				const t2 = touches[1];
				const p1 = getTouchPosition(t1);
				const p2 = getTouchPosition(t2);
				const { distance, angle } = getDistanceAndAngle(p1, p2);

				// scale
				const scaleFactor = distance / multiTouchState.current.initialDistance;
				// rotation
				const angleDiff = angle - multiTouchState.current.initialAngle;

				const rawRotation = multiTouchState.current.initialRotation + angleDiff;
				layer.rotation = maybeSnapAngle(rawRotation, 45, 5);
				layer.rotation = (layer.rotation + 360) % 360;

				if (layer.type === "asset") {
					// asset => width/height
					const newW = Math.max(multiTouchState.current.initialWidth * scaleFactor, 20);
					const newH = Math.max(multiTouchState.current.initialHeight * scaleFactor, 20);

					// Keep same center
					const cx = multiTouchState.current.centerX;
					const cy = multiTouchState.current.centerY;

					layer.width = newW;
					layer.height = newH;
					layer.x = cx - newW / 2;
					layer.y = cy - newH / 2;
				} else if (layer.type === "text") {
					// text => change fontSize
					// The ratio approach:
					const newFontSize = Math.max(multiTouchState.current.initialFontSize * scaleFactor, 10);
					layer.fontSize = newFontSize;
					// Keep center
					layer.x = multiTouchState.current.centerX;
					layer.y = multiTouchState.current.centerY;
				}

				drawCanvas();
			}
		},
		[handleMouseMove, getTouchPosition, drawCanvas]
	);

	const handleTouchEnd = useCallback(
		(e) => {
			if (e.target === canvasRef.current) {
				e.preventDefault();
			}

			// If the user was pinching, stop now
			if (multiTouchState.current.isPinching) {
				multiTouchState.current.isPinching = false;
			}
			handleMouseUp(e);
		},
		[handleMouseUp]
	);

	// ========== Delete selected item ==========

	const handleDeleteSelected = useCallback(() => {
		const selectedIndex = layers.current.findIndex((l) => l.isSelected);
		if (selectedIndex >= 0) {
			const removedLayer = layers.current[selectedIndex];
			layers.current.splice(selectedIndex, 1);

			// If it's text, clear relevant input
			if (removedLayer.type === "text") {
				if (removedLayer.extraIndex == null) {
					// top or bottom
					if (removedLayer.isTop) {
						setTopTextInput("");
					} else {
						setBottomTextInput("");
					}
				} else {
					// extra text
					setExtraTextInputs((prev) => {
						const copy = [...prev];
						copy[removedLayer.extraIndex] = "";
						return copy;
					});
				}
			}
			drawCanvas();
			forceUpdateLayers();
		}
	}, [drawCanvas]);

	// Because multiple text items can have different colors, we only
	// show the toggle as "white" if the selected text is white, etc.
	const updateSelectedTextColorToggle = () => {
		const textLayer = layers.current.find((l) => l.isSelected && l.type === "text");
		if (!textLayer) {
			setSelectedTextIsWhite(true);
		} else {
			setSelectedTextIsWhite(textLayer.color === "#ffffff");
		}
	};

	// ========== Keyboard Support for Delete Key ==========

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Delete" && isAnyItemSelected()) {
				handleDeleteSelected();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleDeleteSelected]);

	// ========== Setup Canvas & Listeners ==========

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const screenWidth = window.innerWidth;

		if (screenWidth <= 768) {
			// Mobile
			const desiredSize = Math.floor(screenWidth * 0.92);
			canvas.width = desiredSize;
			canvas.height = desiredSize;
		} else if (screenWidth <= 1600) {
			// Smaller laptop or small desktop
			canvas.width = 550;
			canvas.height = 550;
		} else {
			// Larger desktop
			canvas.width = 750;
			canvas.height = 750;
		}
		setCanvasHeight(canvas.height);

		// Add your event listeners for mouse/touch
		canvas.addEventListener("mousedown", handleMouseDown);
		canvas.addEventListener("mousemove", handleMouseMove);
		canvas.addEventListener("mouseup", handleMouseUp);
		canvas.addEventListener("mouseout", handleMouseUp);

		canvas.addEventListener("touchstart", handleTouchStart);
		canvas.addEventListener("touchmove", handleTouchMove);
		canvas.addEventListener("touchend", handleTouchEnd);
		canvas.addEventListener("touchcancel", handleTouchEnd);

		return () => {
			canvas.removeEventListener("mousedown", handleMouseDown);
			canvas.removeEventListener("mousemove", handleMouseMove);
			canvas.removeEventListener("mouseup", handleMouseUp);
			canvas.removeEventListener("mouseout", handleMouseUp);

			canvas.removeEventListener("touchstart", handleTouchStart);
			canvas.removeEventListener("touchmove", handleTouchMove);
			canvas.removeEventListener("touchend", handleTouchEnd);
			canvas.removeEventListener("touchcancel", handleTouchEnd);
		};
	}, [
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
	]);

	// Whenever eraser toggles or size changes, re-draw
	useEffect(() => {
		drawCanvas();
	}, [eraserSize, isErasing, drawCanvas]);

	// optional: change cursor when erasing
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.style.cursor = isErasing ? "crosshair" : "default";
	}, [isErasing]);

	// const handleCanvasClick = () => {
	// 	document.getElementById("fileInput")?.click();
	// };

	// ========== Final Return: Use MainLayout ==========

	return (
		<MainLayout
			/* Pass your props into MainLayout so it can choose Desktop or Mobile layout */
			isMobile={isMobile}
			// Tab state
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			//Assets
			assetsAreReady={assetsAreReady}
			setAssetsAreReady={setAssetsAreReady}
			loadingAssets={loadingAssets}
			setLoadingAssets={setLoadingAssets}
			// Canvas + layers
			canvasRef={canvasRef}
			layers={layers}
			drawCanvas={drawCanvas}
			// For text
			selectedTextIsWhite={selectedTextIsWhite}
			setSelectedTextIsWhite={setSelectedTextIsWhite}
			selectedFontFamily={selectedFontFamily}
			setSelectedFontFamily={setSelectedFontFamily}
			isBold={isBold}
			setIsBold={setIsBold}
			isItalic={isItalic}
			setIsItalic={setIsItalic}
			isUnderlined={isUnderlined}
			setIsUnderlined={setIsUnderlined}
			isStrikethrough={isStrikethrough}
			setIsStrikethrough={setIsStrikethrough}
			isAllCaps={isAllCaps}
			setIsAllCaps={setIsAllCaps}
			textOpacity={textOpacity}
			setTextOpacity={setTextOpacity}
			topTextInput={topTextInput}
			setTopTextInput={setTopTextInput}
			bottomTextInput={bottomTextInput}
			setBottomTextInput={setBottomTextInput}
			extraTextInputs={extraTextInputs}
			setExtraTextInputs={setExtraTextInputs}
			clampTextInsideBgRect={clampTextInsideBgRect}
			// BG + Upload
			bgImage={bgImage}
			setBgImage={setBgImage}
			uploadedImage={uploadedImage}
			setUploadedImage={setUploadedImage}
			uploadedImageObj={uploadedImageObj}
			originalBgDataRef={originalBgDataRef}
			handleImageUpload={handleImageUpload}
			handleRemoveBackground={handleRemoveBackground}
			handleSetBackgroundFromUrl={handleSetBackgroundFromUrl}
			handleAddNftAsAsset={handleAddNftAsAsset}
			// Assets
			category={category}
			setCategory={setCategory}
			isAssetAdded={isAssetAdded}
			setIsAssetAdded={setIsAssetAdded}
			// Deep Fry
			preDeepFryCanvasRef={preDeepFryCanvasRef}
			preDeepFryLayersRef={preDeepFryLayersRef}
			satValue={satValue}
			setSatValue={setSatValue}
			conValue={conValue}
			setConValue={setConValue}
			briValue={briValue}
			setBriValue={setBriValue}
			hueValue={hueValue}
			setHueValue={setHueValue}
			pixValue={pixValue}
			setPixValue={setPixValue}
			noiseValue={noiseValue}
			setNoiseValue={setNoiseValue}
			hasDeepFried={hasDeepFried}
			setHasDeepFried={setHasDeepFried}
			satEnabled={satEnabled} 
			setSatEnabled={setSatEnabled}
			conEnabled={conEnabled} 
			setConEnabled={setConEnabled}
			briEnabled={briEnabled} 
			setBriEnabled={setBriEnabled}
			hueEnabled={hueEnabled} 
			setHueEnabled={setHueEnabled}
			pixEnabled={pixEnabled} 
			setPixEnabled={setPixEnabled}
			noiseEnabled={noiseEnabled} 
			setNoiseEnabled={setNoiseEnabled}
			// Selection
			anySelected={anySelected}
			updateAnySelected={updateAnySelected}
			handleDeleteSelected={handleDeleteSelected}
			// Eraser
			isErasing={isErasing}
			setIsErasing={setIsErasing}
			eraserSize={eraserSize}
			setEraserSize={setEraserSize}
			eraserStrokesRef={eraserStrokesRef}
			handleUndoLastErase={handleUndoLastErase}
			handleClearAllErases={handleClearAllErases}
			// BG rect
			bgRect={bgRect}
			// Layers
			forceUpdateLayers={forceUpdateLayers}
			layersVersion={layersVersion}
		/>
	);
}

export default Canvas;
